<template>
  <div class="competitive-intelligence-analysis-page">
    <div class="container-fluid">
      <!-- Dashboard Header -->
      <div class="dashboard-header">
        <div class="row align-items-center">
          <div class="col-md-8">
            <h1 class="title">
              <i class="fas fa-chart-line me-3"></i>
              {{ $t('intelligenceAnalysisPage.title') }}
            </h1>
            <p class="subtitle">{{ $t('intelligenceAnalysisPage.subtitle') }}</p>
          </div>
        </div>
      </div>

      <!-- Analysis Cards Grid -->
      <div class="analysis-grid">
        <!-- Product Pricing Analysis -->
        <div class="analysis-card">
          <div class="analysis-card-header">
            <div class="analysis-card-icon" style="background: linear-gradient(135deg, var(--primary-color), var(--secondary-color));">
              <i class="fas fa-balance-scale"></i>
            </div>
            <h3 class="analysis-card-title">{{ $t('intelligenceAnalysisPage.productPricing.cardTitle') }}</h3>
            <p class="analysis-card-description">{{ $t('intelligenceAnalysisPage.productPricing.cardDescription') }}</p>
          </div>
          <div class="analysis-card-body">
            <div class="form-section">
              <form @submit.prevent="handlePricingAnalysisSubmit">
                <div class="form-row">
                  <label class="form-label" for="pricing_product_id">
                    <i class="fas fa-box me-2"></i>{{ $t('intelligenceAnalysisPage.productPricing.selectProduct') }}
                  </label>
                  <select class="form-select" v-model="pricingForm.productId" id="pricing_product_id" required>
                    <option value="">{{ $t('intelligenceAnalysisPage.productPricing.chooseProductToAnalyze') }}</option>
                    <option v-for="product in adataProductOptions" :key="product.id" :value="product.id">
                      {{ product.name }} ({{ product.brand }})
                    </option>
                  </select>
                  <div class="form-text">{{ $t('intelligenceAnalysisPage.productPricing.selectProductHint') }}</div>
                </div>
                <div class="row">
                  <div class="col-md-6">
                    <div class="form-row">
                      <label class="form-label" for="pricing_competitor_brand">
                        <i class="fas fa-filter me-2"></i>{{ $t('intelligenceAnalysisPage.productPricing.competitorBrandOptional') }}
                      </label>
                                        <select class="form-select" v-model="pricingForm.competitorBrand" id="pricing_competitor_brand">
                                          <option value="">{{ $t('intelligenceAnalysisPage.productPricing.allCompetitors') }}</option>
                                          <option v-for="brand in competitorBrandOptions" :key="brand.name" :value="brand.name">
                                            {{ brand.name }}
                                          </option>
                                        </select>                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-row">
                      <label class="form-label" for="pricing_time_period">
                        <i class="fas fa-calendar-alt me-2"></i>{{ $t('intelligenceAnalysisPage.productPricing.analysisPeriod') }}
                      </label>
                      <select class="form-select" v-model="pricingForm.timePeriod" id="pricing_time_period">
                        <option v-for="option in timePeriodOptions" :key="option.value" :value="option.value">{{ $t(option.textKey) }}</option>
                      </select>
                    </div>
                  </div>
                </div>
                <button type="submit" class="btn btn-primary btn-analyze">
                  <i class="fas fa-chart-line me-2"></i>{{ $t('intelligenceAnalysisPage.productPricing.startAnalysis') }}
                  <span class="spinner-border spinner-border-sm ms-2" :class="{'d-none': !pricingLoading}" role="status"></span>
                </button>
              </form>
            </div>

            <div class="loading-container" v-if="pricingLoading">
              <div class="loading-spinner"></div>
              <p class="loading-text">{{ $t('intelligenceAnalysisPage.productPricing.analyzing') }}</p>
            </div>

            <div class="results-container" v-if="pricingError">
              <div class="alert alert-danger d-flex align-items-center">
                <i class="fas fa-exclamation-triangle me-3 fa-lg"></i>
                <div>
                  <strong>{{ $t('intelligenceAnalysisPage.analysisError') }}</strong><br>
                  {{ pricingError }}
                </div>
              </div>
            </div>

            <div class="results-container" v-if="pricingResults">
              <div class="analysis-result fade-in show">
                <div class="analysis-result-header">
                  <div class="analysis-result-icon" style="background: linear-gradient(135deg, var(--info-color), #007bff);">
                    <i class="fas fa-info-circle"></i>
                  </div>
                  <h3 class="analysis-result-title">{{ $t('intelligenceAnalysisPage.productPricing.overview') }}</h3>
                </div>
                <div class="row">
                  <div class="col-md-6">
                    <p><strong>{{ $t('intelligenceAnalysisPage.productPricing.targetProduct') }}</strong> {{ pricingResults.target_product.product_name }}</p>
                    <p><strong>{{ $t('intelligenceAnalysisPage.productPricing.brand') }}</strong> {{ pricingResults.target_product.brand }}</p>
                    <p><strong>{{ $t('intelligenceAnalysisPage.productPricing.category') }}</strong> {{ pricingResults.target_product.category }}</p>
                  </div>
                  <div class="col-md-6">
                    <p><strong>{{ $t('intelligenceAnalysisPage.productPricing.competitorFilter') }}</strong> {{ pricingResults.competitor_brand_filter || $t('intelligenceAnalysisPage.productPricing.allCompetitors') }}</p>
                    <p><strong>{{ $t('intelligenceAnalysisPage.productPricing.analysisPeriod') }}</strong> {{ $t('intelligenceAnalysisPage.productPricing.last') }} {{ pricingResults.time_period_days }} {{ $t('intelligenceAnalysisPage.productPricing.days') }}</p>
                    <p><strong>{{ $t('intelligenceAnalysisPage.productPricing.analysisDate') }}</strong> {{ new Date().toLocaleDateString() }}</p>
                  </div>
                </div>
              </div>

              <div v-if="pricingResults.pricing_analysis && pricingResults.pricing_analysis.by_source">
                <div class="analysis-result fade-in show" v-for="(sourceData, source) in pricingResults.pricing_analysis.by_source" :key="source">
                  <div class="analysis-result-header">
                    <div class="analysis-result-icon" style="background: linear-gradient(135deg, var(--primary-color), var(--secondary-color));">
                      <i class="fas fa-store"></i>
                    </div>
                    <h3 class="analysis-result-title">{{ source }} {{ $t('intelligenceAnalysisPage.productPricing.channelAnalysis') }}</h3>
                  </div>
                  <div class="metrics-grid" v-if="sourceData.position_analysis">
                    <div class="metric-item">
                      <div class="metric-value">${{ sourceData.target_price }}</div>
                      <div class="metric-label">{{ $t('intelligenceAnalysisPage.productPricing.currentPrice') }}</div>
                    </div>
                    <div class="metric-item">
                      <div class="metric-value">{{ sourceData.position_analysis.rank }}/{{ sourceData.position_analysis.total_competitors + 1 }}</div>
                      <div class="metric-label">{{ $t('intelligenceAnalysisPage.productPricing.priceRanking') }}</div>
                    </div>
                    <div class="metric-item">
                      <div class="metric-value">{{ sourceData.position_analysis.price_percentile ? sourceData.position_analysis.price_percentile.toFixed(1) : 'N/A' }}%</div>
                      <div class="metric-label">{{ $t('intelligenceAnalysisPage.productPricing.pricePercentile') }}</div>
                    </div>
                    <div class="metric-item">
                      <div class="metric-value">{{ sourceData.position_analysis.competitive_intensity }}</div>
                      <div class="metric-label">{{ $t('intelligenceAnalysisPage.productPricing.competitionLevel') }}</div>
                    </div>
                  </div>
                  <div v-if="sourceData.position_analysis.is_lowest" class="alert alert-success"><i class="fas fa-trophy me-2"></i>{{ $t('intelligenceAnalysisPage.productPricing.mostCompetitivePrice') }}</div>
                  <div v-if="sourceData.position_analysis.is_highest" class="alert alert-warning"><i class="fas fa-exclamation-triangle me-2"></i>{{ $t('intelligenceAnalysisPage.productPricing.highestPrice') }}</div>
                </div>
              </div>

              <div class="recommendations-container fade-in show" v-if="pricingResults.pricing_analysis && pricingResults.pricing_analysis.recommendations && pricingResults.pricing_analysis.recommendations.length > 0">
                <h3 class="recommendations-title"><i class="fas fa-lightbulb"></i>{{ $t('intelligenceAnalysisPage.productPricing.strategicRecommendations') }}</h3>
                <div class="recommendation" v-for="(rec, index) in pricingResults.pricing_analysis.recommendations" :key="index">
                  <span class="recommendation-priority priority-medium">{{ $t('intelligenceAnalysisPage.productPricing.strategic') }}</span>
                  <p class="mb-0">{{ rec }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Competitive Threat Assessment -->
        <div class="analysis-card">
          <div class="analysis-card-header">
            <div class="analysis-card-icon" style="background: linear-gradient(135deg, var(--warning-color), #fd7e14);">
              <i class="fas fa-exclamation-triangle"></i>
            </div>
            <h3 class="analysis-card-title">{{ $t('intelligenceAnalysisPage.threatAssessment.cardTitle') }}</h3>
            <p class="analysis-card-description">{{ $t('intelligenceAnalysisPage.threatAssessment.cardDescription') }}</p>
          </div>
          <div class="analysis-card-body">
            <div class="form-section">
              <form @submit.prevent="handleThreatAssessmentSubmit">
                <div class="form-row">
                  <label class="form-label" for="threat_our_brand">{{ $t('intelligenceAnalysisPage.threatAssessment.ourBrand') }}</label>
                  <select class="form-select" v-model="threatForm.ourBrand" id="threat_our_brand" required>
                    <option v-for="brand in adtatBrandOptions" :key="brand.name" :value="brand.name">
                      {{ brand.name }}
                    </option>
                  </select>
                </div>
                <div class="row">
                  <div class="col-md-6">
                    <div class="form-row">
                      <label class="form-label" for="threat_category">{{ $t('intelligenceAnalysisPage.threatAssessment.productCategoryOptional') }}</label>
                      <select class="form-select" v-model="threatForm.category" id="threat_category">
                        <option value="">{{ $t('intelligenceAnalysisPage.threatAssessment.allCategories') }}</option>
                        <option v-for="category in categoryOptions" :key="category.name" :value="category.name">
                          {{ category.name }}
                        </option>
                      </select>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-row">
                      <label class="form-label" for="threat_time_period">{{ $t('intelligenceAnalysisPage.threatAssessment.analysisPeriod') }}</label>
                      <select class="form-select" v-model="threatForm.timePeriod" id="threat_time_period">
                        <option v-for="option in timePeriodOptions" :key="option.value" :value="option.value">{{ $t(option.textKey) }}</option>
                      </select>
                    </div>
                  </div>
                </div>
                <button type="submit" class="btn btn-warning btn-analyze">
                  <i class="fas fa-shield-alt me-2"></i>{{ $t('intelligenceAnalysisPage.threatAssessment.assessThreats') }}
                  <span class="spinner-border spinner-border-sm ms-2" :class="{'d-none': !threatLoading}" role="status"></span>
                </button>
              </form>
            </div>

            <div class="loading-container" v-if="threatLoading">
              <div class="loading-spinner"></div>
              <p class="loading-text">{{ $t('intelligenceAnalysisPage.threatAssessment.analyzing') }}</p>
            </div>

            <div class="results-container" v-if="threatError">
              <div class="alert alert-danger d-flex align-items-center">
                <i class="fas fa-exclamation-triangle me-3 fa-lg"></i>
                <div>
                  <strong>{{ $t('intelligenceAnalysisPage.analysisError') }}</strong><br>
                  {{ threatError }}
                </div>
              </div>
            </div>

            <div class="results-container" v-if="threatResults">
              <div class="result-card fade-in show">
                <h5><i class="fas fa-info-circle text-primary"></i> {{ $t('intelligenceAnalysisPage.threatAssessment.summary') }}</h5>
                <p><strong>{{ $t('intelligenceAnalysisPage.threatAssessment.ourBrand') }}:</strong> {{ threatResults.our_brand }}</p>
                <p><strong>{{ $t('intelligenceAnalysisPage.threatAssessment.analysisCategory') }}:</strong> {{ threatResults.category_filter || $t('intelligenceAnalysisPage.threatAssessment.allCategories') }}</p>
                <p><strong>{{ $t('intelligenceAnalysisPage.threatAssessment.analysisTimeframe') }}:</strong> {{ $t('intelligenceAnalysisPage.productPricing.last') }} {{ threatResults.time_period_days }} {{ $t('intelligenceAnalysisPage.productPricing.days') }}</p>

                <div class="metric-grid" v-if="threatResults.threat_analysis && threatResults.threat_analysis.analysis_summary">
                  <div class="metric-card">
                    <div class="metric-value">{{ threatResults.threat_analysis.analysis_summary.total_competitors }}</div>
                    <div class="metric-label">{{ $t('intelligenceAnalysisPage.threatAssessment.totalCompetitors') }}</div>
                  </div>
                  <div class="metric-card">
                    <div class="metric-value">{{ threatResults.threat_analysis.analysis_summary.analyzed_competitors }}</div>
                    <div class="metric-label">{{ $t('intelligenceAnalysisPage.threatAssessment.analyzedCompetitors') }}</div>
                  </div>
                  <div class="metric-card">
                    <div class="metric-value">{{ threatResults.threat_analysis.analysis_summary.high_threat_competitors }}</div>
                    <div class="metric-label">{{ $t('intelligenceAnalysisPage.threatAssessment.highThreatCompetitors') }}</div>
                  </div>
                </div>
              </div>

              <div class="result-card fade-in show" v-if="threatResults.threat_analysis && threatResults.threat_analysis.main_threat">
                <h5><i class="fas fa-exclamation-triangle text-danger"></i> {{ $t('intelligenceAnalysisPage.threatAssessment.mainThreat') }} {{ threatResults.threat_analysis.main_threat[0] }}</h5>
                <div class="alert alert-danger">
                  <strong>{{ $t('intelligenceAnalysisPage.threatAssessment.threatScore') }} {{ threatResults.threat_analysis.main_threat[1].total_threat_score ? threatResults.threat_analysis.main_threat[1].total_threat_score.toFixed(1) : 'N/A' }}</strong>
                </div>

                <div class="metric-grid">
                  <div class="metric-card">
                    <div class="metric-value">{{ threatResults.threat_analysis.main_threat[1].product_overlap }}</div>
                    <div class="metric-label">{{ $t('intelligenceAnalysisPage.threatAssessment.productOverlap') }}</div>
                  </div>
                  <div class="metric-card">
                    <div class="metric-value">{{ threatResults.threat_analysis.main_threat[1].price_advantage ? threatResults.threat_analysis.main_threat[1].price_advantage.toFixed(1) : 'N/A' }}%</div>
                    <div class="metric-label">{{ $t('intelligenceAnalysisPage.threatAssessment.priceAdvantage') }}</div>
                  </div>
                  <div class="metric-card">
                    <div class="metric-value">{{ threatResults.threat_analysis.main_threat[1].market_presence }}</div>
                    <div class="metric-label">{{ $t('intelligenceAnalysisPage.threatAssessment.marketPresence') }}</div>
                  </div>
                  <div class="metric-card">
                    <div class="metric-value">{{ threatResults.threat_analysis.main_threat[1].sales_volume_strength ? threatResults.threat_analysis.main_threat[1].sales_volume_strength.toFixed(1) : 'N/A' }}</div>
                    <div class="metric-label">{{ $t('intelligenceAnalysisPage.threatAssessment.salesVolumeStrength') }}</div>
                  </div>
                </div>
              </div>

              <div class="result-card fade-in show" v-if="threatResults.threat_analysis && threatResults.threat_analysis.all_threats && threatResults.threat_analysis.all_threats.length > 0">
                <h5><i class="fas fa-list text-info"></i> {{ $t('intelligenceAnalysisPage.threatAssessment.allThreatsRanking') }}</h5>
                <div v-for="([brand, metrics], index) in threatResults.threat_analysis.all_threats" :key="index">
                  <div class="competitor-threat">
                    <div>
                      <strong>{{ brand }}</strong>
                      <br>
                      <small class="text-muted">
                        {{ $t('intelligenceAnalysisPage.threatAssessment.productOverlap') }}: {{ metrics.product_overlap }} | 
                        {{ $t('intelligenceAnalysisPage.threatAssessment.priceAdvantage') }}: {{ metrics.price_advantage ? metrics.price_advantage.toFixed(1) : 'N/A' }}% | 
                        {{ $t('intelligenceAnalysisPage.threatAssessment.marketPresence') }}: {{ metrics.market_presence }}
                      </small>
                    </div>
                    <div class="threat-score" :class="{
                      'threat-high': metrics.total_threat_score > 70,
                      'threat-medium': metrics.total_threat_score > 40 && metrics.total_threat_score <= 70,
                      'threat-low': metrics.total_threat_score <= 40
                    }">
                      {{ metrics.total_threat_score ? metrics.total_threat_score.toFixed(1) : 'N/A' }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Price Positioning Analysis -->
        <div class="analysis-card">
          <div class="analysis-card-header">
            <div class="analysis-card-icon" style="background: linear-gradient(135deg, var(--success-color), #20c997);">
              <i class="fas fa-map-marker-alt"></i>
            </div>
            <h3 class="analysis-card-title">{{ $t('intelligenceAnalysisPage.pricePositioning.cardTitle') }}</h3>
            <p class="analysis-card-description">{{ $t('intelligenceAnalysisPage.pricePositioning.cardDescription') }}</p>
          </div>
          <div class="analysis-card-body">
            <div class="form-section">
              <form @submit.prevent="handlePositioningAnalysisSubmit">
                <div class="form-row">
                  <label class="form-label" for="positioning_product_id">
                    <i class="fas fa-box me-2"></i>{{ $t('intelligenceAnalysisPage.pricePositioning.selectProduct') }}
                  </label>
                  <select class="form-select" v-model="positioningForm.productId" id="positioning_product_id">
                    <option value="">{{ $t('intelligenceAnalysisPage.pricePositioning.chooseProductToAnalyze') }}</option>
                    <option v-for="product in adataProductOptions" :key="product.id" :value="product.id">
                      {{ product.name }} ({{ product.brand }})
                    </option>
                  </select>
                  <div class="form-text">{{ $t('intelligenceAnalysisPage.pricePositioning.selectProductHint') }}</div>
                </div>







                                <div class="row">







                                  <div class="col-md-6">







                                    <div class="form-row">







                                      <label class="form-label" for="positioning_brand_filter">







                                        <i class="fas fa-filter me-2"></i>{{ $t('intelligenceAnalysisPage.pricePositioning.brandOptional') }}







                                      </label>







                                      <select class="form-select" v-model="positioningForm.brand" id="positioning_brand_filter">







                                        <option value="">{{ $t('intelligenceAnalysisPage.pricePositioning.allBrands') }}</option>







                                        <option v-for="brand in competitorBrandOptions" :key="brand.name" :value="brand.name">







                                          {{ brand.name }}







                                        </option>







                                      </select>







                                    </div>







                                  </div>







                                  <div class="col-md-6">







                                    <div class="form-row">







                                      <label class="form-label" for="positioning_time_period">







                                        <i class="fas fa-calendar-alt me-2"></i>{{ $t('intelligenceAnalysisPage.productPricing.analysisPeriod') }}







                                      </label>







                                      <select class="form-select" v-model="positioningForm.timePeriod" id="positioning_time_period">







                                        <option v-for="option in timePeriodOptions" :key="option.value" :value="option.value">{{ $t(option.textKey) }}</option>







                                      </select>







                                    </div>







                                  </div>







                                </div>

                <button type="submit" class="btn btn-success btn-analyze">
                  <i class="fas fa-crosshairs me-2"></i>{{ $t('intelligenceAnalysisPage.pricePositioning.analyzePosition') }}
                  <span class="spinner-border spinner-border-sm ms-2" :class="{'d-none': !positioningLoading}" role="status"></span>
                </button>
              </form>
            </div>

            <div class="loading-container" v-if="positioningLoading">
              <div class="loading-spinner"></div>
              <p class="loading-text">{{ $t('intelligenceAnalysisPage.pricePositioning.analyzing') }}</p>
            </div>

            <div class="results-container" v-if="positioningError">
              <div class="alert alert-danger d-flex align-items-center">
                <i class="fas fa-exclamation-triangle me-3 fa-lg"></i>
                <div>
                  <strong>{{ $t('intelligenceAnalysisPage.analysisError') }}</strong><br>
                  {{ positioningError }}
                </div>
              </div>
            </div>

                        <div class="results-container" v-if="positioningResults">
                          <div class="result-card fade-in show">
                            <h5><i class="fas fa-info-circle text-primary"></i> {{ $t('intelligenceAnalysisPage.pricePositioning.summary') }}</h5>
                            <p v-if="positioningResults.product_id"><strong>{{ $t('intelligenceAnalysisPage.pricePositioning.targetProduct') }}</strong> {{ positioningResults.product_id }}</p>
                            <p v-if="positioningResults.competitor_product_ids && positioningResults.competitor_product_ids.length > 0"><strong>{{ $t('intelligenceAnalysisPage.pricePositioning.competitorProducts') }}</strong> {{ positioningResults.competitor_product_ids.join(', ') }}</p>
                            <p v-if="positioningResults.brand"><strong>{{ $t('intelligenceAnalysisPage.pricePositioning.analysisBrand') }}</strong> {{ positioningResults.brand }}</p>
                            <p v-if="positioningResults.category_filter"><strong>{{ $t('intelligenceAnalysisPage.pricePositioning.productCategory') }}</strong> {{ positioningResults.category_filter }}</p>
                            <p v-if="positioningResults.start_date && positioningResults.end_date"><strong>{{ $t('intelligenceAnalysisPage.pricePositioning.analysisPeriod') }}</strong> {{ positioningResults.start_date }} - {{ positioningResults.end_date }} ({{ positioningResults.time_period_days }} {{ $t('intelligenceAnalysisPage.productPricing.days') }})</p>
                            <p v-if="positioningResults.source_platform"><strong>{{ $t('intelligenceAnalysisPage.pricePositioning.sourcePlatform') }}</strong> {{ positioningResults.source_platform }}</p>
                            <p v-if="positioningResults.time_granularity"><strong>{{ $t('intelligenceAnalysisPage.pricePositioning.timeGranularity') }}</strong> {{ positioningResults.time_granularity }}</p>
                          </div>
            
                          <div class="result-card fade-in show" v-if="positioningResults?.positioning_analysis?.channel_comparison">
                            <div v-for="(channelData, channel) in positioningResults.positioning_analysis.channel_comparison" :key="channel">
                              <h5 v-if="Object.keys(channelData).length > 0"><i class="fas fa-store text-info"></i> {{ channel }} {{ $t('intelligenceAnalysisPage.pricePositioning.channelAnalysis') }}</h5>
                              <div class="metric-grid" v-if="Object.keys(channelData).length > 0">
                                <div class="metric-card">
                                  <div class="metric-value">{{ channelData.products ? channelData.products.length : 0 }}</div>
                                  <div class="metric-label">{{ $t('intelligenceAnalysisPage.pricePositioning.productsOnSale') }}</div>
                                </div>
                                <div class="metric-card">
                                  <div class="metric-value">{{ channelData.avg_discount ? channelData.avg_discount.toFixed(1) : 'N/A' }}%</div>
                                  <div class="metric-label">{{ $t('intelligenceAnalysisPage.pricePositioning.averageDiscount') }}</div>
                                </div>
                                <div class="metric-card">
                                  <div class="metric-value">{{ channelData.availability_rate ? channelData.availability_rate.toFixed(1) : 'N/A' }}%</div>
                                  <div class="metric-label">{{ $t('intelligenceAnalysisPage.pricePositioning.stockAvailability') }}</div>
                                </div>
                              </div>
                              <p v-if="channelData.price_distribution">
                                <strong>{{ $t('intelligenceAnalysisPage.pricePositioning.priceDistribution') }}</strong>
                                {{ $t('intelligenceAnalysisPage.pricePositioning.min') }} ${{ channelData.price_distribution.min }} -
                                {{ $t('intelligenceAnalysisPage.pricePositioning.max') }} ${{ channelData.price_distribution.max }} |
                                {{ $t('intelligenceAnalysisPage.pricePositioning.avg') }} ${{ channelData.price_distribution.avg ? channelData.price_distribution.avg.toFixed(2) : 'N/A' }}
                              </p>
                            </div>
                          </div>
            
                          <div class="result-card fade-in show" v-if="positioningResults?.positioning_analysis?.positioning_strategy">
                            <h5><i class="fas fa-chart-line text-success"></i> {{ $t('intelligenceAnalysisPage.pricePositioning.crossChannelPricingStrategy') }}</h5>
                            <p v-if="positioningResults?.positioning_analysis?.positioning_strategy.pricing_strategy">
                              <strong>{{ $t('intelligenceAnalysisPage.pricePositioning.pricingStrategy') }}</strong> {{ positioningResults.positioning_analysis.positioning_strategy.pricing_strategy }}
                            </p>
                            <p v-if="positioningResults?.positioning_analysis?.positioning_strategy">
                              <strong>{{ $t('intelligenceAnalysisPage.pricePositioning.channelPriceDifference') }}</strong> ${{ typeof positioningResults.positioning_analysis?.positioning_strategy?.price_difference === 'number' ? positioningResults.positioning_analysis.positioning_strategy.price_difference.toFixed(2) : 'N/A' }}
                              ({{ typeof positioningResults.positioning_analysis?.positioning_strategy?.price_difference_percentage === 'number' ? positioningResults.positioning_analysis.positioning_strategy.price_difference_percentage.toFixed(1) : 'N/A' }}%)
                            </p>
                            <div class="row" v-if="positioningResults?.positioning_analysis?.positioning_strategy.discount_comparison">
                              <div class="col-md-6">
                                <div class="metric-card">
                                  <div class="metric-value">{{ positioningResults.positioning_analysis?.positioning_strategy?.discount_comparison.target_avg_discount ? positioningResults.positioning_analysis.positioning_strategy.discount_comparison.target_avg_discount.toFixed(1) : 'N/A' }}%</div>
                                  <div class="metric-label">{{ $t('intelligenceAnalysisPage.pricePositioning.targetAvgDiscount') }}</div>
                                </div>
                              </div>
                              <div class="col-md-6">
                                <div class="metric-value">{{ positioningResults?.positioning_analysis?.positioning_strategy.discount_comparison.competitor_avg_discount ? positioningResults.positioning_analysis.positioning_strategy.discount_comparison.competitor_avg_discount.toFixed(1) : 'N/A' }}%</div>
                                  <div class="metric-label">{{ $t('intelligenceAnalysisPage.pricePositioning.competitorAvgDiscount') }}</div>
                                </div>
                              </div>
                            </div>
            
                          <div class="result-card fade-in show" v-if="positioningResults?.positioning_analysis?.recommendations?.length > 0">
                            <h5><i class="fas fa-lightbulb text-warning"></i> {{ $t('intelligenceAnalysisPage.pricePositioning.recommendations') }}</h5>
                            <div class="recommendation-item" v-for="(rec, index) in positioningResults?.positioning_analysis?.recommendations" :key="index">
                              <i class="fas fa-arrow-right"></i> {{ rec }}
                            </div>
                          </div>
                        </div>          </div>
        </div>

        <!-- SKU Competitive Landscape -->
        <div class="analysis-card">
          <div class="analysis-card-header">
            <div class="analysis-card-icon" style="background: linear-gradient(135deg, var(--info-color), #007bff);">
              <i class="fas fa-sitemap"></i>
            </div>
            <h3 class="analysis-card-title">{{ $t('intelligenceAnalysisPage.skuLandscape.cardTitle') }}</h3>
            <p class="analysis-card-description">{{ $t('intelligenceAnalysisPage.skuLandscape.cardDescription') }}</p>
          </div>
          <div class="analysis-card-body">
            <div class="form-section">
              <form @submit.prevent="handleSKULandscapeSubmit">
                <div class="form-row">
                  <label class="form-label" for="landscape_product_id">{{ $t('intelligenceAnalysisPage.skuLandscape.selectProductSku') }}</label>
                  <select class="form-select" v-model="skuLandscapeForm.productId" id="landscape_product_id" required>
                    <option value="">{{ $t('intelligenceAnalysisPage.skuLandscape.chooseProductToAnalyze') }}</option>
                    <option v-for="product in adataProductOptions" :key="product.id" :value="product.id">
                      {{ product.name }} ({{ product.brand }} - {{ product.category }})
                    </option>
                  </select>
                </div>
                <div class="form-row">
                  <label class="form-label" for="landscape_time_period">{{ $t('intelligenceAnalysisPage.skuLandscape.analysisPeriod') }}</label>
                  <select class="form-select" v-model="skuLandscapeForm.timePeriod" id="landscape_time_period">
                    <option v-for="option in timePeriodOptions" :key="option.value" :value="option.value">{{ $t(option.textKey) }}</option>
                  </select>
                </div>
                <button type="submit" class="btn btn-info btn-analyze">
                  <i class="fas fa-network-wired me-2"></i>{{ $t('intelligenceAnalysisPage.skuLandscape.analyzeLandscape') }}
                  <span class="spinner-border spinner-border-sm ms-2" :class="{'d-none': !skuLandscapeLoading}" role="status"></span>
                </button>
              </form>
            </div>

            <div class="loading-container" v-if="skuLandscapeLoading">
              <div class="loading-spinner"></div>
              <p class="loading-text">{{ $t('intelligenceAnalysisPage.skuLandscape.analyzing') }}</p>
            </div>

            <div class="results-container" v-if="skuLandscapeError">
              <div class="alert alert-danger d-flex align-items-center">
                <i class="fas fa-exclamation-triangle me-3 fa-lg"></i>
                <div>
                  <strong>{{ $t('intelligenceAnalysisPage.analysisError') }}</strong><br>
                  {{ skuLandscapeError }}
                </div>
              </div>
            </div>

            <div class="results-container" v-if="skuLandscapeResults">
              <div class="result-card fade-in show">
                <h5><i class="fas fa-info-circle text-primary"></i> {{ $t('intelligenceAnalysisPage.skuLandscape.skuDetails') }}</h5>
                <p><strong>{{ $t('intelligenceAnalysisPage.skuLandscape.product') }}</strong> {{ skuLandscapeResults.target_product.product_name }}</p>
                <p><strong>{{ $t('intelligenceAnalysisPage.skuLandscape.brand') }}</strong> {{ skuLandscapeResults.target_product.brand }}</p>
                <p><strong>{{ $t('intelligenceAnalysisPage.skuLandscape.category') }}</strong> {{ skuLandscapeResults.target_product.category }}</p>
                <p><strong>{{ $t('intelligenceAnalysisPage.skuLandscape.model') }}</strong> {{ skuLandscapeResults.target_product.product_model || 'N/A' }}</p>
                <p><strong>{{ $t('intelligenceAnalysisPage.skuLandscape.analysisTimeframe') }}</strong> {{ $t('intelligenceAnalysisPage.productPricing.last') }} {{ skuLandscapeResults.time_period_days }} {{ $t('intelligenceAnalysisPage.productPricing.days') }}</p>
              </div>

              <div class="result-card fade-in show" v-if="skuLandscapeResults.competitive_landscape && skuLandscapeResults.competitive_landscape.direct_competitors && skuLandscapeResults.competitive_landscape.direct_competitors.length > 0">
                <h5><i class="fas fa-users text-warning"></i> {{ $t('intelligenceAnalysisPage.skuLandscape.directCompetitors') }} ({{ skuLandscapeResults.competitive_landscape.direct_competitors.length }})</h5>
                <div v-for="(comp, index) in skuLandscapeResults.competitive_landscape.direct_competitors.slice(0, 10)" :key="index">
                  <div class="competitor-threat">
                    <div>
                      <strong>{{ comp.product.product_name }}</strong> ({{ comp.product.brand }})
                      <br>
                      <small class="text-muted">{{ $t('intelligenceAnalysisPage.skuLandscape.model') }} {{ comp.product.product_model || 'N/A' }}</small>
                    </div>
                    <div>
                      <small class="badge bg-secondary me-1" v-for="(priceData, source) in comp.prices" :key="source">
                        {{ source }}: ${{ priceData.price }}
                      </small>
                    </div>
                  </div>
                </div>
                <p class="text-muted" v-if="skuLandscapeResults.competitive_landscape.direct_competitors.length > 10">{{ $t('intelligenceAnalysisPage.skuLandscape.onlyShowingTop10') }}</p>
              </div>

              <div class="result-card fade-in show" v-if="skuLandscapeResults.competitive_landscape && skuLandscapeResults.competitive_landscape.category_analysis">
                <h5><i class="fas fa-chart-pie text-info"></i> {{ skuLandscapeResults.target_product.category }} {{ $t('intelligenceAnalysisPage.skuLandscape.categoryAnalysis') }}</h5>
                <div class="metric-grid">
                  <div class="metric-card">
                    <div class="metric-value">{{ skuLandscapeResults.competitive_landscape.category_analysis.total_products }}</div>
                    <div class="metric-label">{{ $t('intelligenceAnalysisPage.skuLandscape.totalProductsInCategory') }}</div>
                  </div>
                  <template v-if="skuLandscapeResults.competitive_landscape.category_analysis.price_analysis && Object.keys(skuLandscapeResults.competitive_landscape.category_analysis.price_analysis).length > 0">
                    <div class="metric-card">
                      <div class="metric-value">${{ skuLandscapeResults.competitive_landscape.category_analysis.price_analysis.avg_price ? skuLandscapeResults.competitive_landscape.category_analysis.price_analysis.avg_price.toFixed(2) : 'N/A' }}</div>
                      <div class="metric-label">{{ $t('intelligenceAnalysisPage.skuLandscape.categoryAveragePrice') }}</div>
                    </div>
                    <div class="metric-card">
                      <div class="metric-value">${{ skuLandscapeResults.competitive_landscape.category_analysis.price_analysis.min_price || 'N/A' }} - ${{ skuLandscapeResults.competitive_landscape.category_analysis.price_analysis.max_price || 'N/A' }}</div>
                      <div class="metric-label">{{ $t('intelligenceAnalysisPage.skuLandscape.priceRange') }}</div>
                    </div>
                  </template>
                </div>
                <p v-if="skuLandscapeResults.competitive_landscape.category_analysis.market_leaders">
                  <strong>{{ $t('intelligenceAnalysisPage.skuLandscape.marketLeaders') }}</strong>
                  {{ skuLandscapeResults.competitive_landscape.category_analysis.market_leaders.map(ml => `${ml[0]}(${ml[1]} ${$t('intelligenceAnalysisPage.skuLandscape.productsCount')})`).join(', ') }}
                </p>
              </div>

              <div class="result-card fade-in show" v-if="skuLandscapeResults.competitive_landscape && skuLandscapeResults.competitive_landscape.price_positioning && Object.keys(skuLandscapeResults.competitive_landscape.price_positioning).length > 0">
                <h5><i class="fas fa-balance-scale text-success"></i> {{ $t('intelligenceAnalysisPage.skuLandscape.pricePositioning') }}</h5>
                <template v-if="skuLandscapeResults.competitive_landscape.price_positioning.target_avg_price">
                  <div class="metric-grid">
                    <div class="metric-card">
                      <div class="metric-value">${{ skuLandscapeResults.competitive_landscape.price_positioning.target_avg_price ? skuLandscapeResults.competitive_landscape.price_positioning.target_avg_price.toFixed(2) : 'N/A' }}</div>
                      <div class="metric-label">{{ $t('intelligenceAnalysisPage.skuLandscape.ourAveragePrice') }}</div>
                    </div>
                    <div class="metric-card">
                      <div class="metric-value">${{ skuLandscapeResults.competitive_landscape.price_positioning.competitor_avg_price ? skuLandscapeResults.competitive_landscape.price_positioning.competitor_avg_price.toFixed(2) : 'N/A' }}</div>
                      <div class="metric-label">{{ $t('intelligenceAnalysisPage.skuLandscape.competitorAveragePrice') }}</div>
                    </div>
                    <div class="metric-card">
                      <div class="metric-value">{{ skuLandscapeResults.competitive_landscape.price_positioning.price_premium_percentage ? skuLandscapeResults.competitive_landscape.price_positioning.price_premium_percentage.toFixed(1) : 'N/A' }}%</div>
                      <div class="metric-label">{{ $t('intelligenceAnalysisPage.skuLandscape.pricePremiumRate') }}</div>
                    </div>
                    <div class="metric-card">
                      <div class="metric-value">{{ skuLandscapeResults.competitive_landscape.price_positioning.position }}</div>
                      <div class="metric-label">{{ $t('intelligenceAnalysisPage.skuLandscape.positioningStrategy') }}</div>
                    </div>
                  </div>

                  <div v-if="skuLandscapeResults.competitive_landscape.price_positioning.position === 'premium'" class="alert alert-info"><i class="fas fa-crown"></i> {{ $t('intelligenceAnalysisPage.skuLandscape.premiumMarket') }}</div>
                  <div v-else-if="skuLandscapeResults.competitive_landscape.price_positioning.position === 'value'" class="alert alert-success"><i class="fas fa-dollar-sign"></i> {{ $t('intelligenceAnalysisPage.skuLandscape.priceAdvantage') }}</div>
                  <div v-else-if="skuLandscapeResults.competitive_landscape.price_positioning.position === 'balanced'" class="alert alert-secondary"><i class="fas fa-equals"></i> {{ $t('intelligenceAnalysisPage.skuLandscape.priceOnPar') }}</div>
                </template>
                <div v-else class="alert alert-info">
                  <i class="fas fa-info-circle"></i>
                  {{ $t('intelligenceAnalysisPage.skuLandscape.noPricePositioningData') }}
                </div>
              </div>

              <div class="result-card fade-in show" v-if="skuLandscapeResults.competitive_landscape && skuLandscapeResults.competitive_landscape.market_share_indicators">
                <h5><i class="fas fa-chart-area text-primary"></i> {{ $t('intelligenceAnalysisPage.skuLandscape.marketShareIndicators') }}</h5>
                <div class="metric-grid">
                  <template v-if="skuLandscapeResults.competitive_landscape.market_share_indicators.estimated_market_share">
                    <div class="metric-card">
                      <div class="metric-value">{{ skuLandscapeResults.competitive_landscape.market_share_indicators.estimated_market_share.toFixed(1) }}%</div>
                      <div class="metric-label">{{ $t('intelligenceAnalysisPage.skuLandscape.estimatedMarketShare') }}</div>
                    </div>
                  </template>
                  <template v-if="skuLandscapeResults.competitive_landscape.market_share_indicators.sales_ranking">
                    <div class="metric-card">
                      <div class="metric-value">#{{ skuLandscapeResults.competitive_landscape.market_share_indicators.sales_ranking }}</div>
                      <div class="metric-label">{{ $t('intelligenceAnalysisPage.skuLandscape.salesRanking') }}</div>
                    </div>
                  </template>
                  <template v-if="skuLandscapeResults.competitive_landscape.market_share_indicators.channel_coverage">
                    <div class="metric-card">
                      <div class="metric-value">{{ skuLandscapeResults.competitive_landscape.market_share_indicators.channel_coverage.toFixed(1) }}%</div>
                      <div class="metric-label">{{ $t('intelligenceAnalysisPage.skuLandscape.channelCoverage') }}</div>
                    </div>
                  </template>
                </div>
              </div>

              <div class="result-card fade-in show" v-if="skuLandscapeResults.competitive_landscape && skuLandscapeResults.competitive_landscape.swot_analysis">
                <h5><i class="fas fa-chess text-dark"></i> {{ $t('intelligenceAnalysisPage.skuLandscape.swotAnalysis') }}</h5>
                <div class="row">
                  <div class="col-md-6 mb-3" v-if="skuLandscapeResults.competitive_landscape.swot_analysis.strengths && skuLandscapeResults.competitive_landscape.swot_analysis.strengths.length > 0">
                    <h6 class="text-success"><i class="fas fa-plus-circle"></i> {{ $t('intelligenceAnalysisPage.skuLandscape.strengths') }}</h6>
                    <div class="recommendation-item" style="background: #d4edda; border-left-color: #28a745;" v-for="(s, index) in skuLandscapeResults.competitive_landscape.swot_analysis.strengths" :key="index">{{ s }}</div>
                  </div>

                  <div class="col-md-6 mb-3" v-if="skuLandscapeResults.competitive_landscape.swot_analysis.weaknesses && skuLandscapeResults.competitive_landscape.swot_analysis.weaknesses.length > 0">
                    <h6 class="text-danger"><i class="fas fa-minus-circle"></i> {{ $t('intelligenceAnalysisPage.skuLandscape.weaknesses') }}</h6>
                    <div class="recommendation-item" style="background: #f8d7da; border-left-color: #dc3545;" v-for="(w, index) in skuLandscapeResults.competitive_landscape.swot_analysis.weaknesses" :key="index">{{ w }}</div>
                  </div>

                  <div class="col-md-6 mb-3" v-if="skuLandscapeResults.competitive_landscape.swot_analysis.opportunities && skuLandscapeResults.competitive_landscape.swot_analysis.opportunities.length > 0">
                    <h6 class="text-primary"><i class="fas fa-lightbulb"></i> {{ $t('intelligenceAnalysisPage.skuLandscape.opportunities') }}</h6>
                    <div class="recommendation-item" v-for="(o, index) in skuLandscapeResults.competitive_landscape.swot_analysis.opportunities" :key="index">{{ o }}</div>
                  </div>

                  <div class="col-md-6 mb-3" v-if="skuLandscapeResults.competitive_landscape.swot_analysis.threats && skuLandscapeResults.competitive_landscape.swot_analysis.threats.length > 0">
                    <h6 class="text-warning"><i class="fas fa-exclamation-triangle"></i> {{ $t('intelligenceAnalysisPage.skuLandscape.threats') }}</h6>
                    <div class="recommendation-item" style="background: #fff3cd; border-left-color: #ffc107;" v-for="(t, index) in skuLandscapeResults.competitive_landscape.swot_analysis.threats" :key="index">{{ t }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
/* eslint-disable no-unused-vars */
import { ref, reactive, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { fetchProductOptions, fetchBrandOptions, fetchCategoryOptions, performProductPricingAnalysis, performThreatAssessment, performPricePositioningAnalysis, performSKULandscapeAnalysis } from '../services/ciService';

const { t } = useI18n();

const timePeriodOptions = [
  { value: 7, textKey: 'intelligenceAnalysisPage.productPricing.last7Days' },
  { value: 30, textKey: 'intelligenceAnalysisPage.productPricing.last30Days' },
  { value: 90, textKey: 'intelligenceAnalysisPage.productPricing.last90Days' },
];

// Form data reactivity
const pricingForm = reactive({
  productId: '',
  competitorBrand: '',
  timePeriod: 30,
});

const threatForm = reactive({
  ourBrand: '',
  category: '',
  timePeriod: 30,
});

const positioningForm = reactive({
  productId: '',
  brand: '', // Keep as optional filter
  timePeriod: 30,
});

const skuLandscapeForm = reactive({
  productId: '',
  timePeriod: 30,
});

// Dropdown options reactivity
const adataProductOptions = ref([]); // For ADATA products
const competitorProductOptions = ref([]); // For competitor products
const adtatBrandOptions = ref([]); // For ADATA group brands (Our Brand)
const competitorBrandOptions = ref([]); // For all brands (Competitor Brand)
const categoryOptions = ref([]);

// New: Declare sourcePlatformOptions and timeGranularityOptions
const sourcePlatformOptions = ref([
  { value: '', textKey: 'intelligenceAnalysisPage.common.allPlatforms' },
  { value: 'Amazon', textKey: 'intelligenceAnalysisPage.common.amazon' },
  { value: 'BestBuy', textKey: 'intelligenceAnalysisPage.common.bestbuy' },
]);

const timeGranularityOptions = ref([
  { value: 'daily', textKey: 'intelligenceAnalysisPage.pricePositioning.daily' },
  { value: 'weekly', textKey: 'intelligenceAnalysisPage.pricePositioning.weekly' },
  { value: 'monthly', textKey: 'intelligenceAnalysisPage.pricePositioning.monthly' },
]);

// Analysis results reactivity
const pricingResults = ref(null);
const threatResults = ref(null);
const positioningResults = ref(null);
const skuLandscapeResults = ref(null);

// Loading states reactivity
const pricingLoading = ref(false);
const threatLoading = ref(false);
const positioningLoading = ref(false);
const skuLandscapeLoading = ref(false);

// Error states reactivity
const pricingError = ref(null);
const threatError = ref(null);
const positioningError = ref(null);
const skuLandscapeError = ref(null);

// Fetch dropdown options on component mount
onMounted(async () => {
  try {
    adataProductOptions.value = await fetchProductOptions(true);
    competitorProductOptions.value = await fetchProductOptions(false);
    adtatBrandOptions.value = await fetchBrandOptions(true);
    competitorBrandOptions.value = await fetchBrandOptions(false);
    categoryOptions.value = await fetchCategoryOptions();

    // Set default brand for threat and positioning forms if available
    if (competitorBrandOptions.value.length > 0) {
      threatForm.ourBrand = competitorBrandOptions.value[0].name;
      positioningForm.brand = competitorBrandOptions.value[0].name;
    }



  } catch (error) {
    console.error('Error fetching dropdown options:', error);
    // Handle error, e.g., show a toast notification
  }
});

// Form submission handlers
const handlePricingAnalysisSubmit = async () => {
  pricingLoading.value = true;
  pricingError.value = null;
  pricingResults.value = null;
  try {
    const response = await performProductPricingAnalysis({
      product_id: pricingForm.productId,
      competitor_brand: pricingForm.competitorBrand || null,
      time_period_days: pricingForm.timePeriod,
    });
    pricingResults.value = response;
  } catch (error) {
    console.error('Pricing Analysis failed:', error);
    pricingError.value = error.response?.data?.detail || error.message;
  } finally {
    pricingLoading.value = false;
  }
};

const handleThreatAssessmentSubmit = async () => {
  threatLoading.value = true;
  threatError.value = null;
  threatResults.value = null;
  try {
    const response = await performThreatAssessment({
      our_brand: threatForm.ourBrand,
      category: threatForm.category || null,
      time_period_days: threatForm.timePeriod,
    });
    threatResults.value = response;
  } catch (error) {
    console.error('Threat Assessment failed:', error);
    threatError.value = error.response?.data?.detail || error.message;
  } finally {
    threatLoading.value = false;
  }
};

const handlePositioningAnalysisSubmit = async () => {
  positioningLoading.value = true;
  positioningError.value = null;
  positioningResults.value = null;
  try {
    const now = new Date();
    let startDate = null;
    let endDate = now;

    if (positioningForm.timePeriod) {
      startDate = new Date(now);
      startDate.setDate(now.getDate() - positioningForm.timePeriod);
    }

    const payload = {
      product_id: positioningForm.productId || null,
      brand: positioningForm.brand || null,
      start_date: startDate ? startDate.toISOString().split('T')[0] : null,
      end_date: endDate ? endDate.toISOString().split('T')[0] : null,
    };
    // Filter out null values to send cleaner payload
    Object.keys(payload).forEach(key => payload[key] === null && delete payload[key]);

    const response = await performPricePositioningAnalysis(payload);
    positioningResults.value = response;
  } catch (error) {
    console.error('Price Positioning Analysis failed:', error);
    positioningError.value = error.response?.data?.detail || error.message;
  } finally {
    positioningLoading.value = false;
  }
};


const handleSKULandscapeSubmit = async () => {
  skuLandscapeLoading.value = true;
  skuLandscapeError.value = null;
  skuLandscapeResults.value = null;
  try {
    const response = await performSKULandscapeAnalysis({
      product_id: skuLandscapeForm.productId,
      time_period_days: skuLandscapeForm.timePeriod,
    });
    skuLandscapeResults.value = response;
  } catch (error) {
    console.error('SKU Landscape Analysis failed:', error);
    skuLandscapeError.value = error.response?.data?.detail || error.message;
  } finally {
    skuLandscapeLoading.value = false;
  }
};

// Utility functions (will be moved or refactored later)
// For now, keeping them minimal or removing if not directly used in template

</script>

<style scoped>
/* Scoped styles for this component */
.competitive-intelligence-analysis-page {
  padding: 20px;
}

/* Custom styles from original HTML, adapted for Vue component */
.dashboard-header {
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 1px solid #eee;
}

.dashboard-header .title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #343a40;
}

.dashboard-header .subtitle {
  font-size: 1.1rem;
  color: #6c757d;
}

.analysis-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 25px;
}

.analysis-card {
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  display: flex;
  flex-direction: column;
  margin-bottom: 0;
}

.analysis-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 6px 25px rgba(0, 0, 0, 0.1);
}

.analysis-card-header {
  padding: 25px;
  display: flex;
  align-items: center;
  gap: 20px;
  background-color: #f8f9fa;
  border-bottom: 1px solid #eee;
}

.analysis-card-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 2rem;
  color: #fff;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
}

.analysis-card-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #343a40;
  margin-bottom: 5px;
}

.analysis-card-description {
  font-size: 0.9rem;
  color: #6c757d;
  margin-bottom: 0;
}

.analysis-card-body {
  padding: 25px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
}

.form-section {
  margin-bottom: 25px;
  padding-bottom: 25px;
  border-bottom: 1px solid #eee;
}

.form-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.form-row {
  margin-bottom: 15px;
}

.form-label {
  font-weight: 500;
  color: #495057;
  margin-bottom: 8px;
  display: block;
}

.btn-analyze {
  width: 100%;
  padding: 12px 20px;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 8px;
  margin-top: 15px;
  transition: all 0.3s ease;
}

.btn-analyze:hover {
  opacity: 0.9;
  transform: translateY(-2px);
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 30px;
  color: #6c757d;
  /* display: none; */ /* Hidden by default */
}

.loading-spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-left-color: #007bff;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin-bottom: 15px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-text {
  font-size: 1.1rem;
}

.results-container {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #eee;
  /* display: none; */ /* Hidden by default */
}

.analysis-result {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
  border: 1px solid #e9ecef;
  opacity: 0;
  transform: translateY(20px);
  transition: opacity 0.5s ease, transform 0.5s ease;
}

.analysis-result.show {
  opacity: 1;
  transform: translateY(0);
}

.analysis-result-header {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 15px;
}

.analysis-result-icon {
  width: 45px;
  height: 45px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 1.5rem;
  color: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.analysis-result-title {
  font-size: 1.3rem;
  font-weight: 600;
  color: #343a40;
  margin-bottom: 0;
}

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 15px;
  margin-top: 15px;
  margin-bottom: 20px;
}

.metric-item {
  background-color: #e9ecef;
  border-radius: 8px;
  padding: 15px;
  text-align: center;
}

.metric-value {
  font-size: 1.8rem;
  font-weight: 700;
  color: #007bff;
  margin-bottom: 5px;
}

.metric-label {
  font-size: 0.9rem;
  color: #6c757d;
}

.recommendations-container {
  background-color: #e6f7ff; /* Light blue background */
  border: 1px solid #91d5ff; /* Blue border */
  border-radius: 8px;
  padding: 20px;
  margin-top: 20px;
}

.recommendations-title {
  color: #0056b3; /* Darker blue for title */
  font-size: 1.4rem;
  margin-bottom: 15px;
}

.recommendation {
  background-color: #fff;
  border-left: 5px solid #007bff; /* Blue left border */
  padding: 10px 15px;
  margin-bottom: 10px;
  border-radius: 5px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.recommendation-priority {
  font-weight: 600;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.8rem;
  color: #fff;
}

.priority-medium {
  background-color: #ffc107; /* Warning yellow */
}

/* Specific styles for threat assessment results */
.result-card {
    background-color: #f8f9fa;
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 20px;
    border: 1px solid #e9ecef;
}

.metric-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    gap: 15px;
    margin-top: 15px;
    margin-bottom: 20px;
}

.metric-card {
    background-color: #e9ecef;
    border-radius: 8px;
    padding: 15px;
    text-align: center;
}

.competitor-threat {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 0;
    border-bottom: 1px dashed #dee2e6;
}

.competitor-threat:last-child {
    border-bottom: none;
}

.threat-score {
    font-weight: 700;
    padding: 5px 10px;
    border-radius: 5px;
    color: #fff;
}

.threat-high {
    background-color: #dc3545; /* Red */
}

.threat-medium {
    background-color: #ffc107; /* Orange */
}

.threat-low {
    background-color: #28a745; /* Green */
}

.recommendation-item {
    background-color: #fff;
    border-left: 4px solid #007bff;
    padding: 10px 15px;
    margin-bottom: 10px;
    border-radius: 5px;
    display: flex;
    align-items: center;
    gap: 10px;
}

/* SWOT Analysis specific styles */
.swot-analysis .recommendation-item {
    border-left-color: #6c757d; /* Default for SWOT items */
}

/* Define CSS variables for colors if not already defined globally */
:root {
  --primary-color: #007bff;
  --secondary-color: #6c757d;
  --success-color: #28a745;
  --info-color: #17a2b8;
  --warning-color: #ffc107;
  --danger-color: #dc3545;
}
</style>